// src/components/TenantBrandingLoader.tsx
// Fetches the tenant's branding from the backend and overlays it onto the
// hardcoded brandConfig (Tenant.branding = runtime source of truth, brandConfig
// = pre-fetch default — see applyTenantBranding in brandConfig.ts). Mount
// inside ApolloProvider AND inside ChakraProvider, above everything that
// renders brand values; children render immediately with the hardcoded
// defaults and re-render once the overlay lands. Same pattern as
// IpfsGatewayLoader.
//
// Colour mode (2026-09-02): the tenant's `colorMode` is a DEFAULT, not a
// mandate. It is applied when the viewer has no stored preference, or when
// their stored value is still the tenant default we applied last time (i.e.
// they never toggled). A viewer who toggled keeps their choice.

import React, { createContext, useContext, useEffect, useState } from "react";
import { useQuery, gql } from "@apollo/client";
import { useColorMode } from "@chakra-ui/react";
import { applyTenantBranding, brandConfig } from "../brandConfig";

// Bumped once the overlay has been applied. `brandConfig` is mutated in place,
// and a wrapper's setState does NOT re-render children it merely passes
// through (same element identity → React bails out), so components that read
// brandConfig at render time subscribe to this instead — see src/Logo.tsx.
const BrandVersionContext = createContext(0);
export const useBrandVersion = () => useContext(BrandVersionContext);

const COLOR_MODE_KEY = "chakra-ui-color-mode";
const TENANT_DEFAULT_KEY = "chakra-ui-color-mode-tenant-default";

const GET_TENANT_BRANDING = gql`
  query GetTenantBrandingForApp {
    currentTenant {
      id
      branding {
        siteName
        tagline
        description
        favicon
        logo
        logoOnDark
        logoOnLight
        logoSquare
        logoGlyph
        primaryColor
        secondaryColor
        accentColor
        primaryColorHover
        secondaryColorHover
        backgroundMain
        colorMode
        fontHeading
        fontBody
        hideSiteName
        hideTagline
      }
      emailConfig {
        fromEmail
      }
    }
  }
`;

export const TenantBrandingLoader = ({ children }: { children: React.ReactNode }) => {
  const [brandVersion, setBrandVersion] = useState(0);
  const { colorMode, setColorMode } = useColorMode();
  const { data } = useQuery(GET_TENANT_BRANDING, { fetchPolicy: "cache-first" });

  useEffect(() => {
    const branding = data?.currentTenant?.branding;
    if (!branding) return;
    applyTenantBranding(branding, data?.currentTenant?.emailConfig?.fromEmail);

    const wanted = branding.colorMode ? String(branding.colorMode).toLowerCase() : null;
    if (wanted === "light" || wanted === "dark") {
      let stored: string | null = null;
      let lastDefault: string | null = null;
      try {
        stored = localStorage.getItem(COLOR_MODE_KEY);
        lastDefault = localStorage.getItem(TENANT_DEFAULT_KEY);
      } catch {
        /* storage unavailable — treat as no preference */
      }
      const viewerNeverToggled = !stored || stored === lastDefault;
      // A locked brand (one palette only) always wins over the viewer's stored choice.
      const locked = brandConfig.colorModeLocked === true;
      if ((locked || viewerNeverToggled) && colorMode !== wanted) setColorMode(wanted);
      try {
        localStorage.setItem(TENANT_DEFAULT_KEY, wanted);
      } catch {
        /* ignore */
      }
    }

    // brandConfig is mutated in place — publish a new version so subscribers re-read it.
    setBrandVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return <BrandVersionContext.Provider value={brandVersion}>{children}</BrandVersionContext.Provider>;
};
